// Centralized DYNAMIC market presentation registry.
// Never hardcode UI to a single market. Unknown/future backend codes must render
// gracefully via readable transformation.

export type MarketCategory = "GOALS" | "RESULT" | "BTTS" | "FIRST_HALF" | "TEAM" | "OTHER";
export type MarketRisk = "LOW" | "MEDIUM" | "HIGH" | "LIVE";

export interface MarketDef {
  code: string;
  category: MarketCategory;
  risk: MarketRisk;
  icon: string; // lucide-react icon name
  tr: string;
  en: string;
}

// Known mapping (NOT exhaustive — backend can expand freely).
export const MARKET_REGISTRY: Record<string, MarketDef> = {
  ONE_MORE_GOAL: { code: "ONE_MORE_GOAL", category: "GOALS", risk: "LIVE", icon: "Zap", tr: "Maçta Bir Gol Daha", en: "One More Goal" },
  NEXT_GOAL: { code: "NEXT_GOAL", category: "GOALS", risk: "LIVE", icon: "Zap", tr: "Sıradaki Gol", en: "Next Goal" },
  HOME_NEXT_GOAL: { code: "HOME_NEXT_GOAL", category: "TEAM", risk: "LIVE", icon: "Home", tr: "Sıradaki Gol: Ev Sahibi", en: "Next Goal: Home" },
  AWAY_NEXT_GOAL: { code: "AWAY_NEXT_GOAL", category: "TEAM", risk: "LIVE", icon: "Plane", tr: "Sıradaki Gol: Deplasman", en: "Next Goal: Away" },
  HOME_SCORE: { code: "HOME_SCORE", category: "TEAM", risk: "MEDIUM", icon: "Home", tr: "Ev Sahibi Gol Atar", en: "Home to Score" },
  AWAY_SCORE: { code: "AWAY_SCORE", category: "TEAM", risk: "MEDIUM", icon: "Plane", tr: "Deplasman Gol Atar", en: "Away to Score" },
  OVER_1_5: { code: "OVER_1_5", category: "GOALS", risk: "LOW", icon: "TrendingUp", tr: "1.5 Üst", en: "Over 1.5" },
  OVER_2_5: { code: "OVER_2_5", category: "GOALS", risk: "MEDIUM", icon: "TrendingUp", tr: "2.5 Üst", en: "Over 2.5" },
  OVER_3_5: { code: "OVER_3_5", category: "GOALS", risk: "HIGH", icon: "TrendingUp", tr: "3.5 Üst", en: "Over 3.5" },
  UNDER_3_5: { code: "UNDER_3_5", category: "GOALS", risk: "LOW", icon: "TrendingDown", tr: "3.5 Alt", en: "Under 3.5" },
  BTTS: { code: "BTTS", category: "BTTS", risk: "MEDIUM", icon: "ArrowLeftRight", tr: "Karşılıklı Gol", en: "Both Teams to Score" },
  BTTS_YES: { code: "BTTS_YES", category: "BTTS", risk: "MEDIUM", icon: "ArrowLeftRight", tr: "Karşılıklı Gol Var", en: "BTTS: Yes" },
  BTTS_NO: { code: "BTTS_NO", category: "BTTS", risk: "MEDIUM", icon: "ArrowLeftRight", tr: "Karşılıklı Gol Yok", en: "BTTS: No" },
  DOUBLE_CHANCE_1X: { code: "DOUBLE_CHANCE_1X", category: "RESULT", risk: "LOW", icon: "Shield", tr: "1X Çifte Şans", en: "Double Chance 1X" },
  DOUBLE_CHANCE_X2: { code: "DOUBLE_CHANCE_X2", category: "RESULT", risk: "LOW", icon: "Shield", tr: "X2 Çifte Şans", en: "Double Chance X2" },
  DOUBLE_CHANCE_12: { code: "DOUBLE_CHANCE_12", category: "RESULT", risk: "LOW", icon: "Shield", tr: "12 Çifte Şans", en: "Double Chance 12" },
  HOME_WIN: { code: "HOME_WIN", category: "RESULT", risk: "MEDIUM", icon: "Home", tr: "Ev Sahibi Kazanır", en: "Home Win" },
  AWAY_WIN: { code: "AWAY_WIN", category: "RESULT", risk: "MEDIUM", icon: "Plane", tr: "Deplasman Kazanır", en: "Away Win" },
  DRAW: { code: "DRAW", category: "RESULT", risk: "HIGH", icon: "Equal", tr: "Beraberlik", en: "Draw" },
  FH_OVER_0_5: { code: "FH_OVER_0_5", category: "FIRST_HALF", risk: "LOW", icon: "Clock", tr: "İlk Yarı 0.5 Üst", en: "1st Half Over 0.5" },
  FH_OVER_1_5: { code: "FH_OVER_1_5", category: "FIRST_HALF", risk: "MEDIUM", icon: "Clock", tr: "İlk Yarı 1.5 Üst", en: "1st Half Over 1.5" },
  FH_HOME_SCORE: { code: "FH_HOME_SCORE", category: "FIRST_HALF", risk: "MEDIUM", icon: "Clock", tr: "İlk Yarı Ev Sahibi Gol", en: "1st Half Home to Score" },
  FH_AWAY_SCORE: { code: "FH_AWAY_SCORE", category: "FIRST_HALF", risk: "MEDIUM", icon: "Clock", tr: "İlk Yarı Deplasman Gol", en: "1st Half Away to Score" },
};

export const CATEGORY_LABEL: Record<MarketCategory, { tr: string; en: string }> = {
  GOALS: { tr: "Goller", en: "Goals" },
  RESULT: { tr: "Maç Sonucu", en: "Match Result" },
  BTTS: { tr: "Karşılıklı Gol", en: "Both Teams" },
  FIRST_HALF: { tr: "İlk Yarı", en: "First Half" },
  TEAM: { tr: "Takım", en: "Team" },
  OTHER: { tr: "Diğer", en: "Other" },
};

const RISK_LABEL: Record<MarketRisk, { tr: string; en: string }> = {
  LOW: { tr: "Düşük Risk", en: "Low Risk" },
  MEDIUM: { tr: "Orta Risk", en: "Medium Risk" },
  HIGH: { tr: "Yüksek Risk", en: "High Risk" },
  LIVE: { tr: "Canlı", en: "Live" },
};

// Readable transform for unknown codes: OVER_2_5 -> "Over 2 5" style cleanup.
function humanize(code: string): string {
  return code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b(\d)\s(\d)\b/g, "$1.$2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface ResolvedMarket {
  code: string;
  label: string;
  category: MarketCategory;
  risk: MarketRisk;
  riskLabel: string;
  icon: string;
  known: boolean;
}

// Preferred fallback priority:
// 1) backend-provided display label  2) centralized known mapping  3) readable transform
export function resolveMarket(
  code: string | null | undefined,
  lang: "tr" | "en" = "tr",
  backendLabel?: string | null,
): ResolvedMarket {
  const safeCode = (code || "UNKNOWN").toUpperCase();
  const def = MARKET_REGISTRY[safeCode];
  const backend = backendLabel && !isRawCode(backendLabel, safeCode) ? backendLabel : null;
  const label = backend || (def ? def[lang] : humanize(safeCode));
  const category = def?.category || "OTHER";
  const risk = def?.risk || "MEDIUM";
  return {
    code: safeCode,
    label,
    category,
    risk,
    riskLabel: RISK_LABEL[risk][lang],
    icon: def?.icon || "Target",
    known: !!def,
  };
}

// backend sometimes echoes the raw code as the "name" (e.g. market_name === market_code)
function isRawCode(label: string, code: string): boolean {
  return label.trim().toUpperCase().replace(/\s+/g, "_") === code;
}
